import { yupResolver } from '@hookform/resolvers/yup';
import { FormContainer } from '@openx/components-core/lib/FormContainer/FormContainer.tsx';
import { scrollToError } from '@openx/components-core/utils/scrollToError.ts';
import { onKeyDown } from '@openx/components-form/onKeyDown.ts';
import { useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import type { CuratedDealBuyerDiscountApi } from '../../../types/curatedDealBuyerDiscount.ts';
import { CuratedDealBuyerDiscountsFormSections } from './CuratedDealBuyerDiscountsFormSections.tsx';
import { curatedDealBuyerDiscountsFormDefaultValues } from './constants.ts';
import { useCuratedDealBuyerDiscountFormSubmit } from './hooks/useCuratedDealBuyerDiscountFormSubmit.ts';
import type { CuratedDealBuyerDiscountSeat, CuratedDealBuyerDiscountsFormData } from './types.ts';
import { createCuratedDealBuyerDiscountFormData } from './utils/createCuratedDealBuyerDiscountFormData.ts';
import { getCuratedDealBuyerDiscountsValidationSchema } from './validationSchema.ts';

type CuratedDealBuyerDiscountsFormProps = {
  curatedDealBuyerDiscount?: CuratedDealBuyerDiscountApi;
  demandPartnerName: string | null;
  seatById: Record<string, CuratedDealBuyerDiscountSeat>;
};

export const CuratedDealBuyerDiscountsForm = ({
  curatedDealBuyerDiscount,
  demandPartnerName,
  seatById,
}: CuratedDealBuyerDiscountsFormProps) => {
  const isEdit = !!curatedDealBuyerDiscount;
  const { t } = useTranslation();

  const defaultValues = useMemo(() => {
    if (isEdit) {
      return createCuratedDealBuyerDiscountFormData(curatedDealBuyerDiscount, demandPartnerName, seatById);
    }

    return curatedDealBuyerDiscountsFormDefaultValues;
  }, [isEdit, curatedDealBuyerDiscount, demandPartnerName, seatById]);

  const validationSchema = useMemo(() => getCuratedDealBuyerDiscountsValidationSchema(t), [t]);

  const { submitHandler } = useCuratedDealBuyerDiscountFormSubmit(defaultValues, curatedDealBuyerDiscount?.uid);
  const formMethods = useForm<CuratedDealBuyerDiscountsFormData>({
    defaultValues,
    mode: 'all',
    resolver: yupResolver(validationSchema),
  });

  return (
    <FormProvider {...formMethods}>
      <form autoComplete="off" onKeyDown={onKeyDown} onSubmit={formMethods.handleSubmit(submitHandler, scrollToError)}>
        <FormContainer fullPage>
          <CuratedDealBuyerDiscountsFormSections isEdit={isEdit} seatIds={curatedDealBuyerDiscount?.seat_ids} />
        </FormContainer>
      </form>
    </FormProvider>
  );
};
